import { FC, useMemo, useState } from "react";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card } from "@hostingbv/lib/src/components/Card";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { useModal } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { AddCustomerContactModal } from "../modals/AddCustomerContactModal";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { EditCustomerModal } from "../modals/EditCustomerModal";
import { useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { EditCustomerContactModal } from "../modals/EditCustomerContactModal";
import countries from "i18n-iso-countries";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { SectionDivider } from "@hostingbv/lib/src/components/content/SectionDivider";
import { PillSelect } from "@hostingbv/lib/src/components/form/PillSelect";
import { DisplayMode, DisplayModeToggle } from "@hostingbv/lib/src/components/data/DisplayModeToggle";
import { usePersistentState } from "@hostingbv/lib/src/util/usePersistentState";
import { HoverHint } from "@hostingbv/lib/src/components/content/HoverHint";
import { ContextMenu } from "@hostingbv/lib/src/components/content/ContextMenu";
import { InviteCustomerContactModal } from "../modals/InviteCustomerContactModal";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { RevokeCustomerContactAccessModal } from "../modals/RevokeCustomerContactAccessModal";
import {
  faBuilding,
  faEnvelopeOpenText,
  faHashtag,
  faKey,
  faLocationPin,
  faPencil,
  faPeopleGroup,
  faPhone,
  faPlus,
  faUserCheck,
  faUserLock,
  faUserShield,
  faUserSlash,
} from "@fortawesome/free-solid-svg-icons";
import { PageIconHeader } from "@hostingbv/lib/src/components/PageIconHeader";
import { ResetMFAModal } from "../modals/ResetMFAModal";
import { EditCustomerContactRoleModal } from "../modals/EditCustomerContactRoleModal";
import { DeleteCustomerContactModal } from "../modals/DeleteCustomerContactModal";
import { Customer } from "@hostingbv/lib/src/api/common/models/Customer";
import { Contact } from "@hostingbv/lib/src/api/common/models/Contact";

export const CustomerDetails: FC = () => {
  const { uuid } = useParams();
  const { partnerClient } = useApiClient(config);
  const { resource, reload } = useFetchedResource(() => partnerClient.getCustomer(uuid ?? ""));
  const customer: Customer | null = useMemo(() => (resource?.success === true ? resource.data : null), [resource]);

  useRefreshEffect(() => {
    reload();
  });

  return (
    <ContentContainer>
      <PageIconHeader icon={faPeopleGroup}>
        <Breadcrumbs crumbs={[{ label: "Customers", href: "/customers" }]} currentPage={"Customer details"} />
        <PageHeader>Customer details</PageHeader>
      </PageIconHeader>
      <Callout>
        <SectionHeader>Manage your customer</SectionHeader>
        Domains, sites and other resources are registered under a customer. Some products, like domain names may need
        very specific information about the customer.
      </Callout>
      {customer && (
        <>
          <CustomerDetailsCard customer={customer} />
          <SectionDivider />
          <CustomerContactsList customer={customer} />
        </>
      )}
    </ContentContainer>
  );
};

const CustomerDetailsCard: FC<{ customer: Customer }> = ({ customer }) => {
  const countryOptions = countries.getNames("en");
  const { open: openEditModal } = useModal({ title: "Edit customer", body: <EditCustomerModal customer={customer} /> });
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  if (!customer) {
    return <div className={"animate-pulse bg-slate-200 rounded-sm h-48"}></div>;
  }
  return (
    <Card title={"Customer Details"}>
      <div className={"flex items-stretch"}>
        <div className={"flex-1"}>
          <div className={"flex items-center mb-2"}>
            <FontAwesomeIcon
              icon={faBuilding}
              className={"w-4 h-4 mr-3 text-brand-500 p-2 bg-brand-100 rounded-full"}
            />
            <div>
              <div className={"font-medium"}>{customer.displayName}</div>
            </div>
          </div>

          <div className={"flex items-center mb-2"}>
            <FontAwesomeIcon
              icon={faLocationPin}
              className={"w-4 h-4 mr-3 text-brand-500 p-2 bg-brand-100 rounded-full"}
            />
            <div>
              <div className={"font-medium"}>
                {customer.addressStreet} {customer.addressNumber}, {customer.addressZip} {customer.addressCity},{" "}
                {countryOptions[customer.addressCountry] ?? customer.addressCountry}
              </div>
            </div>
          </div>

          <div className={"flex items-center mb-2"}>
            <FontAwesomeIcon icon={faHashtag} className={"w-4 h-4 mr-3 text-brand-500 p-2 bg-brand-100 rounded-full"} />
            <div>
              <div className={"font-medium"}>{customer.referenceNumber ?? "-"}</div>
            </div>
          </div>
        </div>
        {can("edit", "customers", customer.permissions) && (
          <div>
            <Button text={"Edit"} icon={faPencil} type={"secondary"} size={"sm"} onClick={() => openEditModal()} />
          </div>
        )}
      </div>
    </Card>
  );
};

const CustomerContactsList: FC<{ customer: Customer }> = ({ customer }) => {
  const { open: openAddModal } = useModal({
    title: "Add contact",
    body: <AddCustomerContactModal customer={customer} />,
  });

  const [filter, setFilter] = useState<string>("all");

  const whoisContacts = useMemo(
    () =>
      customer.contacts.filter((contact) => ["primary", "financial", "technical", "security"].includes(contact.type)),
    [customer],
  );
  const internalContacts = useMemo(
    () => customer.contacts.filter((contact) => contact.type === "internal"),
    [customer],
  );
  const loginContacts = useMemo(() => customer.contacts.filter((contact) => contact.hasUser), [customer]);
  const filteredContacts = useMemo(() => {
    if (filter === "whois") {
      return whoisContacts;
    }
    if (filter === "internal") {
      return internalContacts;
    }
    if (filter === "login") {
      return loginContacts;
    }
    return customer.contacts;
  }, [filter, whoisContacts, internalContacts, loginContacts]);

  const [displayMode, setDisplayMode] = usePersistentState<DisplayMode>("contact_list_display_mode", "grid");
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);

  return (
    <>
      <div>
        <SectionHeader>Contacts</SectionHeader>
      </div>
      <div className={"flex justify-between"}>
        <PillSelect
          value={filter}
          onChange={setFilter}
          options={{
            all: `All (${customer.contacts.length})`,
            whois: `WHOIS (${whoisContacts.length})`,
            login: `Login access (${loginContacts.length})`,
            internal: `Internal (${internalContacts.length})`,
          }}
        />
        <DisplayModeToggle value={displayMode} onChange={setDisplayMode} />
      </div>
      {displayMode === "grid" ? (
        <>
          <div className={"grid grid-cols-3 -mr-4"}>
            {filteredContacts.map((contact) => (
              <CustomerContactTile customer={customer} contact={contact} key={contact.id} />
            ))}
            {can("create", "customerContacts") && (
              <button
                className={
                  "rounded-sm border border-dashed border-brand-300 hover:bg-brand-50 text-brand-700 text-sm px-6 py-4 mr-4 mb-4"
                }
                onClick={() => openAddModal()}
              >
                <FontAwesomeIcon icon={faPlus} className={"mr-2"} />
                <span className={"font-medium"}>Add contact</span>
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <div className={"bg-white border border-slate-100 rounded-sm"}>
            <table className={"w-full table"}>
              <tbody>
                {filteredContacts.map((contact, index) => (
                  <CustomerContactTableRow key={index} customer={customer} contact={contact} />
                ))}
              </tbody>
            </table>
            {can("create", "customerContacts") && (
              <button
                className={"hover:bg-brand-50 text-brand-700 w-full text-sm px-4 py-3 rounded-b text-left"}
                onClick={() => openAddModal()}
              >
                <FontAwesomeIcon icon={faPlus} className={"mr-2"} />
                <span className={"font-medium"}>Add contact</span>
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
};

const CustomerContactTile: FC<{ customer: Customer; contact: Contact }> = ({ customer, contact }) => {
  const options = useCustomerContactContextMenuOptions(customer, contact);
  return (
    <div key={contact.id} className={"bg-white rounded-sm border border-slate-100 px-6 py-4 mr-4 mb-4"}>
      <div className={"flex items-start justify-between"}>
        <div>
          <div className={"font-medium"}>
            {contact.firstName} {contact.lastName}
          </div>
          <div className={"flex items-center space-x-2 mt-1"}>
            <div
              className={
                "text-brand-700 text-xs uppercase tracking-wide border border-brand-300 inline px-1 rounded-sm"
              }
            >
              {contact.type}
            </div>

            {!contact.hasUser ? (
              <HoverHint hint={"No login access"}>
                <div className={"h-5 w-5 rounded-sm border flex items-center justify-center"}>
                  <FontAwesomeIcon icon={faUserSlash} className={"h-3 w-3 text-slate-300"} />
                </div>
              </HoverHint>
            ) : (
              <HoverHint hint={"Can log in using their email"}>
                <div className={"h-5 w-5 rounded-sm border flex items-center justify-center"}>
                  <FontAwesomeIcon icon={faUserCheck} className={"h-3 w-3 text-brand-800"} />
                </div>
              </HoverHint>
            )}
          </div>
          <a
            href={`mailto:${contact.email}`}
            className={
              "flex items-center h-8 text-xs px-2 mt-1 hover:bg-brand-50 hover:text-brand-800 rounded-sm -mx-2"
            }
          >
            <FontAwesomeIcon icon={faEnvelopeOpenText} className={"mr-2"} />
            {contact.email}
          </a>
          <a
            href={`tel:${contact.phone}`}
            className={
              "flex items-center h-8 text-xs px-2 mt-1 hover:bg-brand-50 hover:text-brand-800 rounded-sm -mx-2"
            }
          >
            <FontAwesomeIcon icon={faPhone} className={"mr-1"} />
            {contact.phone}
          </a>
        </div>
        {options.length > 0 && <ContextMenu options={options} />}
      </div>
    </div>
  );
};

const CustomerContactTableRow: FC<{ customer: Customer; contact: Contact }> = ({ customer, contact }) => {
  const options = useCustomerContactContextMenuOptions(customer, contact);
  return (
    <tr className={"text-sm h-12"}>
      <th className={"px-4 text-left"}>
        {contact.firstName} {contact.lastName}
      </th>
      <td>
        {!contact.hasUser ? (
          <HoverHint hint={"No login access"}>
            <div className={"h-5 w-5 rounded-sm border flex items-center justify-center"}>
              <FontAwesomeIcon icon={faUserSlash} className={"h-3 w-3 text-slate-300"} />
            </div>
          </HoverHint>
        ) : (
          <HoverHint hint={"Can log in using their email"}>
            <div className={"h-5 w-5 rounded-sm border flex items-center justify-center"}>
              <FontAwesomeIcon icon={faUserCheck} className={"h-3 w-3 text-brand-800"} />
            </div>
          </HoverHint>
        )}
      </td>
      <td className={"px-4"}>
        <div
          className={"text-brand-700 text-xs uppercase tracking-wide border border-brand-300 inline px-1 rounded-sm"}
        >
          {contact.type}
        </div>
      </td>
      <td className={"px-4"}>
        <a
          href={`mailto:${contact.email}`}
          className={"flex items-center h-8 text-xs px-2 mt-1 hover:bg-brand-50 hover:text-brand-800 rounded-sm -mx-2"}
        >
          <FontAwesomeIcon icon={faEnvelopeOpenText} className={"mr-2"} />
          {contact.email}
        </a>
      </td>
      <td className={"px-4"}>
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            className={
              "flex items-center h-8 text-xs px-2 mt-1 hover:bg-brand-50 hover:text-brand-800 rounded-sm -mx-2"
            }
          >
            <FontAwesomeIcon icon={faPhone} className={"mr-1"} />
            {contact.phone}
          </a>
        )}
      </td>
      <td className={"px-4 text-right"}>
        {options.length > 0 && (
          <td className={"px-4 text-right"}>
            <ContextMenu options={options} />
          </td>
        )}
      </td>
    </tr>
  );
};

const useCustomerContactContextMenuOptions = (customer: Customer, contact: Contact) => {
  const { open: openEditModal } = useModal({
    title: "Edit contact",
    body: <EditCustomerContactModal customer={customer} contact={contact} />,
  });
  const { open: openDeleteUserModal } = useModal({
    title: "Delete contact",
    body: <DeleteCustomerContactModal customer={customer} contact={contact} />,
  });
  const { open: openInviteUserModal } = useModal({
    title: "Grant login access",
    body: <InviteCustomerContactModal customer={customer} contact={contact} />,
  });
  const { open: openRevokeUserAccessModal } = useModal({
    title: "Revoke login access",
    body: <RevokeCustomerContactAccessModal customer={customer} contact={contact} />,
  });
  const { open: openResetPasswordModal } = useModal({
    title: "Reset user password",
    body: <InviteCustomerContactModal customer={customer} contact={contact} />,
  });
  const { open: openResetMFAModal } = useModal({
    title: "Reset user MFA",
    body: <ResetMFAModal customer={customer} contact={contact} />,
  });
  const { open: openEditRoleModal } = useModal({
    title: "Edit role",
    body: <EditCustomerContactRoleModal customer={customer} contact={contact} />,
  });
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const options = [];

  if (can("edit", "customerContacts", contact.permissions)) {
    options.push({ icon: faPencil, label: "Edit information", onClick: openEditModal });
  }
  // The role belongs to the contact's login, so it can only be edited once the contact has login access.
  if (contact.hasUser && can("edit", "roles") && can("edit", "customerLogins")) {
    options.push({ icon: faUserShield, label: "Edit role", onClick: openEditRoleModal });
  }
  // Logins can only be managed with full access to customers tenant-wide, see CustomerLoginPolicy. Users manage
  // their own password and MFA in Settings.
  const canManageLogin = can("edit", "customerContacts", contact.permissions) && can("edit", "customerLogins");
  if (contact.hasUser && canManageLogin) {
    options.push({ icon: faKey, label: "Reset password", onClick: openResetPasswordModal });
  }
  if (contact.hasUser && canManageLogin) {
    options.push({ icon: faKey, label: "Reset MFA", onClick: openResetMFAModal });
  }
  if (canManageLogin && !contact.hasUser) {
    options.push({ icon: faUserCheck, label: "Grant login access", onClick: openInviteUserModal });
  }
  if (can("delete", "customerContacts", contact.permissions) && !contact.hasUser) {
    options.push({ icon: faUserSlash, label: "Delete contact", onClick: openDeleteUserModal });
  }
  if (canManageLogin && contact.hasUser) {
    options.push({ icon: faUserLock, label: "Revoke login access", onClick: openRevokeUserAccessModal, danger: true });
  }
  return options;
};
